<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { $api } from '@/services';
import { useStore } from 'vuex';
import LoadingDowloadPDF from '@/views/plansDetail/features/reportTarget/loadingDowloadPDF/loadingDowloadPDF.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';

const modalIsOpen = ref(false);
const store = useStore();
const router = useRouter();
const loading = ref(false);

const steps = [
	{ id: 1, label: 'Chọn gói' },
	{ id: 2, label: 'Thanh toán' },
];
const currentStep = ref(1);
const selectedPackageId = ref('');
const quantity = ref(1);
const needInvoice = ref(false);
const invoiceForm = ref({
	tax_id: '',
	company_name: '',
	address: '',
	email: '',
	contact_phone: '',
});

const paymentSubmitted = ref(false);
const paymentProof = ref(null);
const paymentProofPreview = ref('');
const paymentProofInputId = 'buying-data-payment-proof';
const key = ref('');
const contract = ref(null);

const centerName = computed(
	() => store.state.profile?.current_org?.name || '-'
);
const creatorName = computed(
	() => store.state.profile?.staff?.full_name || '-'
);

const selectedPackage = computed(() =>
	packages.value.find((pkg) => pkg.id === selectedPackageId.value)
);

const formatCurrency = (value) => {
	if (value == null || Number.isNaN(value)) return '-';
	return `₫ ${Number(value).toLocaleString('vi-VN')}`;
};

const formatAmount = (value) => {
	if (value == null || Number.isNaN(value)) return '-';
	return Number(value).toLocaleString('vi-VN');
};

const BYTES_PER_MB = 1000000;
const BYTES_PER_GB = 1000000000;

const formatStorageLabel = (sizeBytes, qty = 1) => {
	if (sizeBytes == null || Number.isNaN(Number(sizeBytes))) return '-';
	const totalBytes = Number(sizeBytes) * qty;
	const totalMb = totalBytes / BYTES_PER_MB;
	// ≥ 1000 MB → đổi sang GB
	if (totalMb >= 1000) {
		const totalGb = totalBytes / BYTES_PER_GB;
		const label = Number.isInteger(totalGb)
			? totalGb
			: Math.round(totalGb * 10) / 10;
		return `${label} GB`;
	}
	const label = Number.isInteger(totalMb)
		? totalMb
		: Math.round(totalMb * 10) / 10;
	return `${label} MB`;
};

const summary = computed(() => {
	if (!selectedPackage.value) {
		return {
			label: '-',
			unitPrice: null,
			quantity: quantity.value,
			totalStorage: '-',
			total: null,
		};
	}
	return {
		label: formatStorageLabel(selectedPackage.value.amount, 1),
		unitPrice: selectedPackage.value.price,
		quantity: quantity.value,
		totalStorage: formatStorageLabel(
			selectedPackage.value.amount,
			quantity.value
		),
		total: selectedPackage.value.price * quantity.value,
	};
});

const canContinue = computed(
	() => !!selectedPackage.value && quantity.value > 0
);

const canSubmitPayment = computed(() => !!paymentProof.value);

const decreaseQuantity = () => {
	if (quantity.value > 1) quantity.value -= 1;
};

const increaseQuantity = () => {
	quantity.value += 1;
};

const onQuantityInput = (event) => {
	const value = Number(event.target.value.replace(/\D/g, ''));
	quantity.value = value > 0 ? value : 1;
};

const goBack = () => {
	router.push('/packages');
};

const goToStep = (stepId) => {
	if (paymentSubmitted.value) return;
	if (stepId < currentStep.value) {
		currentStep.value = stepId;
	}
};

const goNext = () => {
	if (currentStep.value === 1 && !canContinue.value) return;
	currentStep.value = Math.min(currentStep.value + 1, steps.length);
};

const copyText = async (text) => {
	try {
		await navigator.clipboard.writeText(text);
		await store.dispatch('setAlertMessage', {
			message: 'Đã sao chép thành công!',
			type: 'success',
		});
	} catch (e) {
		console.error(e);
	}
};

const openPaymentProofPicker = () => {
	const input = document.getElementById(paymentProofInputId);
	if (input) input.click();
};

const onPaymentProofChange = (event) => {
	const file = event?.target?.files?.[0];
	if (!file) return;
	const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
	if (!allowed.includes(file.type)) {
		event.target.value = null;
		return;
	}
	if (paymentProofPreview.value) {
		URL.revokeObjectURL(paymentProofPreview.value);
	}
	paymentProof.value = file;
	paymentProofPreview.value = file.type.startsWith('image/')
		? URL.createObjectURL(file)
		: '';
	event.target.value = null;
};

const removePaymentProof = () => {
	if (paymentProofPreview.value) {
		URL.revokeObjectURL(paymentProofPreview.value);
	}
	paymentProof.value = null;
	paymentProofPreview.value = '';
};

const downloadPaymentProof = () => {
	if (!paymentProofPreview.value && !paymentProof.value) return;
	const link = document.createElement('a');
	if (paymentProofPreview.value) {
		link.href = paymentProofPreview.value;
		link.download = paymentProof.value?.name || 'chung-tu-thanh-toan.png';
	} else {
		link.href = URL.createObjectURL(paymentProof.value);
		link.download = paymentProof.value.name;
	}
	link.click();
};

const getSignatures = async () => {
	try {
		const fileList = [paymentProof.value];
		const {
			data: { data },
		} = await $api.file.getSignatures({
			mime_types: fileList.map((f) => f.type),
			prefix: 'storage_payment_screenshot',
		});
		if (data && data.length) {
			for (let i = 0; i < data.length; i++) {
				const formData = new FormData();
				formData.append(
					'AWSAccessKeyId',
					data[0]['AWSAccessKeyId'] || ''
				);
				formData.append('key', data[i].key || '');
				formData.append('acl', data[i].acl || '');
				formData.append('Content-Type', data[i]['Content-Type'] || '');
				formData.append('x-amz-signature', data[i].signature || '');
				formData.append('Signature', data[i].signature || '');
				formData.append('Policy', data[i].policy || '');
				formData.append(
					'success_action_status',
					data[i].success_action_status || ''
				);
				formData.append('file', fileList[i]);
				await uploadToS3(data[i].action, formData);
			}
		}
	} catch (error) {
		console.error(error);
	}
};

const uploadToS3 = async (url, payload) => {
	try {
		const { data } = await $api.file.uploadToS3(url, payload);
		const keyData = data.toString().match('<Key>(.*)</Key>');
		const assetKey = keyData && keyData.length ? keyData[1] : '';
		key.value = assetKey;
	} catch (e) {
		console.error(e);
	}
};

const submitPayment = async () => {
	if (!canSubmitPayment.value) return;
	loading.value = true;
	try {
		await getSignatures();
		invoiceForm.value.invoice_number = contractPayment.value.invoice_number;
		let params = {
			media_quota_package_id: selectedPackage.value.id,
			payment_screenshot_asset_key: key.value,
			quantity: quantity.value,
			invoice: invoiceForm.value,
		};
		const res = await $api.leader.createQuotaPayment(params);
		contract.value = res.data.data;
		paymentSubmitted.value = true;
	} catch (e) {
		console.log(e);
		store.dispatch('setAlertMessage', {
			message: 'Có lỗi xảy ra, vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		loading.value = false;
	}
};

const downloadInvoice = async () => {
	const contractId = contract.value?.id;
	if (!contractId) {
		store.dispatch('setAlertMessage', {
			message: 'Hoá đơn chưa sẵn sàng để tải xuống',
			type: 'warning',
		});
		return;
	}

	const url =
		process.env.VUE_APP_PDF_URL +
		'packages/invoice/' +
		contractId +
		'?token=' +
		localStorage.getItem('tk') +
		'&subdomain=' +
		localStorage.getItem('sub-domain');
	const params = {
		url: url,
		format: 'a2',
		id_render: 'form_pdf',
	};
	try {
		modalIsOpen.value = true;
		const response = await fetch(
			'https://g4ridpplxi.execute-api.ap-southeast-1.amazonaws.com/',
			{
				method: 'POST',
				body: JSON.stringify(params),
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		);
		if (!response.ok) {
			modalIsOpen.value = false;
			window.open(url + '&is_print=true', '_blank');
			return;
		}
		const data = await response.json();
		if (data.data) {
			modalIsOpen.value = false;
			const linkSource = `data:application/pdf;base64, ${data.data}`;
			const link = document.createElement('a');
			link.href = linkSource;
			link.download = 'Hoadon.pdf';
			link.click();
		}
	} catch (e) {
		modalIsOpen.value = false;
		window.open(url + '&is_print=true', '_blank');
		console.log(e);
	}
};

const getBillingInfo = async () => {
	try {
		const res = await $api.leader.getBillingInfo();
		if (res.data.data !== null) {
			invoiceForm.value = res.data.data;
			needInvoice.value = true;
		}
	} catch (error) {
		console.log(error);
	}
};

const contractPayment = ref({
	qr_code_url: '',
	account_holder_name: '',
	account_number: '',
	bank_name: '',
	branch: '',
	swift_code: '',
	transfer_details: '',
});

const transferContent = computed(() => {
	if (contractPayment.value?.transfer_details) {
		return contractPayment.value.transfer_details;
	}
	return `${
		centerName.value || '[Tên trung tâm]'
	} thanh toan hoa don so [Mã hóa đơn lấy tự động từ admin]`;
});

watch(currentStep, (newVal) => {
	if (newVal === 2) {
		getContractPayment();
	}
});

const packages = ref([]);

const getListQuota = async () => {
	try {
		const res = await $api.leader.getListQuota();
		packages.value = res.data.data?.packages;
	} catch (error) {
		console.log(error);
	}
};

const getContractPayment = async () => {
	try {
		let params = {
			type: 'media_quota_package',
		};
		const res = await $api.leader.getContractPayment(
			params,
			needInvoice.value ? 1 : 2
		);
		contractPayment.value = res.data.data;
	} catch (error) {
		console.log(error);
	}
};

onMounted(() => {
	getListQuota();
	getBillingInfo();
});
</script>

<template>
	<DaModalFrame v-model:open="modalIsOpen">
		<LoadingDowloadPDF :text="'Đang tải xuống file PDF hoá đơn...'" />
	</DaModalFrame>
	<div class="buying-data px-12 md-down:px-4 pb-10 pt-10 md-down:pt-6">
		<div
			class="buying-data__header relative flex items-center gap-2 mb-8 md-down:mb-6"
		>
			<button
				type="button"
				class="buying-data__back flex-shrink-0 cursor-pointer md-down:absolute md-down:left-0 md-down:top-1/2"
				@click="goBack"
			>
				<img
					class="w-4 h-4"
					src="@/assets/images/icons/arrow-right.png"
					alt="back"
				/>
			</button>
			<h1
				class="text-4xl font-bold text-neutral-500 md-down:text-xl md-down:text-center md-down:px-8"
			>
				Mua thêm dung lượng
			</h1>
		</div>
		<div
			class="grid grid-cols-12 md-down:grid-cols-1 gap-6 md-down:gap-4 items-start"
		>
			<div
				class="col-span-8 md-down:col-span-full md-down:order-2 w-full bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden"
			>
				<div class="stepper mb-0">
					<div class="flex w-full md-down:flex-col md-down:min-w-0">
						<div
							v-for="(step, index) in steps"
							:key="step.id"
							class="step-item flex items-center gap-3 px-6 py-3.5 relative flex-1 md-down:flex-none md-down:w-full md-down:px-4 md-down:py-3.5"
							:class="{
								'step-item--active':
									!paymentSubmitted &&
									currentStep === step.id,
								'step-item--done':
									paymentSubmitted || currentStep > step.id,
								'step-item--clickable':
									!paymentSubmitted && step.id < currentStep,
							}"
							@click="goToStep(step.id)"
						>
							<div
								class="step-number w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
							>
								<svg
									v-if="
										paymentSubmitted ||
										currentStep > step.id
									"
									width="14"
									height="10"
									viewBox="0 0 14 10"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M1 5L5 9L13 1"
										stroke="white"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
								<template v-else>
									{{ String(step.id).padStart(2, '0') }}
								</template>
							</div>
							<div
								class="step-label text-sm font-semibold whitespace-nowrap"
							>
								{{ step.label }}
							</div>

							<svg
								v-if="index < steps.length - 1"
								class="step-divider md-down:hidden"
								fill="none"
								height="72"
								viewBox="0 0 12 72"
								width="12"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<mask
									:id="'data-step-chevron-mask-' + step.id"
									fill="black"
									height="72"
									maskUnits="userSpaceOnUse"
									width="13"
									x="-1"
									y="0"
								>
									<rect
										fill="white"
										height="72"
										width="13"
										x="-1"
									/>
									<path d="M0 0V27.2195L10 36L0 44.7805V72" />
								</mask>
								<path
									d="M0 27.2195H-1V27.6722L-0.659801 27.971L0 27.2195ZM10 36L10.6598 36.7514L11.5156 36L10.6598 35.2486L10 36ZM0 44.7805L-0.659801 44.029L-1 44.3278V44.7805H0ZM-1 0V27.2195H1V0H-1ZM-0.659801 27.971L9.3402 36.7514L10.6598 35.2486L0.659801 26.4681L-0.659801 27.971ZM9.3402 35.2486L-0.659801 44.029L0.659801 45.5319L10.6598 36.7514L9.3402 35.2486ZM-1 44.7805V72H1V44.7805H-1Z"
									fill="#CBD5E1"
									:mask="
										'url(#data-step-chevron-mask-' +
										step.id +
										')'
									"
								/>
							</svg>
						</div>
					</div>
				</div>

				<!-- Step 1: Chọn gói -->
				<div v-if="currentStep === 1 && !paymentSubmitted">
					<div class="p-6 md-down:p-4">
						<div
							class="flex items-center justify-between mb-6 md-down:mb-4 gap-2"
						>
							<div
								class="text-lg font-semibold text-neutral-600 md-down:text-base"
							>
								Chọn gói dung lượng
							</div>
							<a
								href="https://www.luca.education/chi-phi"
								target="_blank"
								rel="noopener"
								class="text-sm text-green-600 underline flex-shrink-0"
							>
								Tìm hiểu thêm
							</a>
						</div>

						<div class="space-y-3 mb-6">
							<label
								v-for="pkg in packages"
								:key="pkg.id"
								class="flex items-center gap-4 md-down:gap-3 p-4 md-down:p-3.5 rounded-xl border cursor-pointer transition"
								:class="
									selectedPackageId === pkg.id
										? 'border-yellow-500 bg-white shadow-sm'
										: 'border-neutral-200 bg-white hover:border-neutral-300'
								"
							>
								<input
									v-model="selectedPackageId"
									class="text-yellow-500 border-neutral-400 border-2 focus:ring-yellow-500 h-4 w-4 flex-shrink-0"
									type="radio"
									name="buying-data-package"
									:value="pkg.id"
								/>
								<div
									class="flex-1 min-w-0 flex items-center justify-between gap-3"
								>
									<div
										class="flex items-center gap-2 flex-wrap min-w-0"
									>
										<span
											class="font-semibold text-neutral-700 text-base"
										>
											{{ pkg.title }}
										</span>
										<span
											v-if="pkg.id === 5"
											class="bg-neutral-700 text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
										>
											Tiết kiệm nhất
										</span>
									</div>
									<span
										class="font-semibold text-neutral-700 flex-shrink-0"
									>
										{{ formatCurrency(pkg.price) }}
									</span>
								</div>
							</label>
						</div>

						<div class="mb-4 max-w-xs md-down:max-w-full">
							<label
								class="block text-neutral-700 mb-2 md-down:text-sm"
							>
								Số lượng
							</label>
							<div
								class="flex items-center border border-neutral-200 rounded-lg overflow-hidden bg-white focus-within:border-yellow-500 focus-within:ring-1 focus-within:ring-yellow-500"
							>
								<button
									type="button"
									class="w-12 h-12 text-xl text-neutral-500 hover:bg-neutral-50"
									@click="decreaseQuantity"
								>
									−
								</button>
								<input
									class="flex-1 h-12 text-center border-0 text-neutral-700 font-semibold focus:outline-none focus:ring-0 bg-neutral-50"
									:value="quantity"
									inputmode="numeric"
									@input="onQuantityInput"
								/>
								<button
									type="button"
									class="w-12 h-12 text-xl text-neutral-500 hover:bg-neutral-50"
									@click="increaseQuantity"
								>
									+
								</button>
							</div>
						</div>

						<p class="text-neutral-500 mb-8 md-down:mb-6">
							Dung lượng video mua thêm không có thời hạn sử dụng
							và sẽ được chuyển tiếp/ cộng dồn sang gói tiếp theo
						</p>

						<div class="border-t border-neutral-100 pt-6">
							<label
								class="flex items-center gap-3 cursor-pointer select-none mb-4"
							>
								<input
									v-model="needInvoice"
									type="checkbox"
									class="h-5 w-5 rounded border-neutral-300 text-yellow-500 focus:ring-yellow-500"
								/>
								<span class="text-neutral-700"
									>Bạn có nhu cầu xuất hoá đơn</span
								>
							</label>

							<div v-if="needInvoice">
								<div
									class="text-lg font-semibold text-neutral-700 mb-4 md-down:text-base"
								>
									Thông tin xuất hoá đơn
								</div>
								<div
									class="grid grid-cols-1 md:grid-cols-2 gap-4"
								>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Mã số thuế</label
										>
										<input
											v-model="invoiceForm.tax_id"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập mã số thuế"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Tên công ty</label
										>
										<input
											v-model="invoiceForm.company_name"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập tên công ty"
										/>
									</div>
									<div class="md:col-span-2">
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Địa chỉ</label
										>
										<input
											v-model="invoiceForm.address"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập địa chỉ"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Email nhận hóa đơn</label
										>
										<input
											v-model="invoiceForm.email"
											type="email"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập email nhận hóa đơn"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>SĐT liên hệ</label
										>
										<input
											v-model="invoiceForm.contact_phone"
											type="tel"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập SĐT liên hệ"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div
						class="p-6 md-down:p-4 flex justify-end border-t border-neutral-100"
					>
						<button
							type="button"
							class="w-1/2 md-down:w-full h-12 rounded-lg font-semibold transition"
							:class="
								canContinue
									? 'button-p'
									: 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
							"
							:disabled="!canContinue"
							@click="goNext"
						>
							Tiếp tục
						</button>
					</div>
				</div>

				<!-- Step 2: Thanh toán -->
				<div v-else-if="currentStep === 2 && !paymentSubmitted">
					<input
						:id="paymentProofInputId"
						type="file"
						hidden
						accept="image/png,image/jpeg,image/jpg,application/pdf"
						@change="onPaymentProofChange"
					/>

					<div class="p-6 md:p-8 md-down:p-4">
						<div
							class="text-lg font-semibold text-neutral-600 mb-6 md-down:mb-4 md-down:text-base"
						>
							Thanh toán chuyển khoản
						</div>

						<div
							class="flex flex-col items-center mb-8 md-down:mb-6"
						>
							<img
								v-if="contractPayment?.qr_code_url"
								:src="contractPayment.qr_code_url"
								alt="VietQR"
								class="w-56 h-56 md-down:w-full md-down:max-w-[280px] md-down:h-auto object-contain bg-white"
							/>
							<div
								v-else
								class="w-56 h-56 md-down:w-full md-down:max-w-[280px] bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-400 text-sm"
							>
								Đang tải QR...
							</div>
						</div>

						<div
							class="max-w-2xl mx-auto space-y-4 text-sm md-down:space-y-3"
						>
							<div
								class="flex justify-between gap-4 items-start md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400 flex-shrink-0"
									>Số tài khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left flex items-center gap-2"
								>
									{{ contractPayment.account_number || '-' }}
									<button
										v-if="contractPayment.account_number"
										type="button"
										class="flex-shrink-0"
										@click="
											copyText(
												contractPayment.account_number
											)
										"
									>
										<img
											src="@/assets/images/icons/copy.png"
											alt="copy"
											class="w-4 h-4"
										/>
									</button>
								</span>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Tên chủ tài khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{
										contractPayment.account_holder_name ||
										'-'
									}}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Tên ngân hàng</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{
										contractPayment.bank_name || '-'
									}}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 items-start md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400 flex-shrink-0"
									>Nội dung chuyển khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left flex items-start gap-2 break-words"
								>
									<span class="min-w-0">{{
										transferContent
									}}</span>
									<button
										type="button"
										class="flex-shrink-0 mt-0.5"
										@click="copyText(transferContent)"
									>
										<img
											src="@/assets/images/icons/copy.png"
											alt="copy"
											class="w-4 h-4"
										/>
									</button>
								</span>
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md:p-8 md-down:p-4"
					>
						<div
							class="text-lg font-semibold text-neutral-700 mb-4 md-down:text-base"
						>
							Chứng từ thanh toán
						</div>

						<div
							v-if="!paymentProof"
							class="w-full rounded-xl border border-dashed border-neutral-200 bg-neutral-50 py-10 px-6 md-down:py-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-yellow-500 transition"
							@click="openPaymentProofPicker"
						>
							<img
								src="@/assets/images/icons/cloud-upload-fill.png"
								alt=""
								class="w-8 h-8 mb-3"
							/>
							<div class="font-semibold text-neutral-700 mb-1">
								Tải lên chứng từ thanh toán
							</div>
							<div class="text-sm text-neutral-400">
								PNG, JPG & PDF
							</div>
						</div>
						<div v-else class="relative inline-block md-down:block">
							<button
								type="button"
								class="absolute top-2 right-2 cursor-pointer z-10"
								@click="removePaymentProof"
							>
								<img
									src="@/assets/images/icons/close.png"
									alt="remove"
									class="w-6 h-6"
								/>
							</button>
							<img
								v-if="paymentProofPreview"
								:src="paymentProofPreview"
								alt="Chứng từ thanh toán"
								class="max-w-xs max-h-64 md-down:max-w-full rounded-lg object-contain border border-neutral-200"
							/>
							<div
								v-else
								class="px-6 py-8 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-600 text-sm"
							>
								{{ paymentProof.name }}
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md-down:p-4 flex justify-end"
					>
						<button
							type="button"
							class="h-12 px-10 rounded-lg font-semibold transition w-1/2 md-down:w-full"
							:class="
								canSubmitPayment
									? 'button-p'
									: 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
							"
							:disabled="!canSubmitPayment || loading"
							@click="submitPayment"
						>
							Nộp
						</button>
					</div>
				</div>

				<!-- Success -->
				<div v-else>
					<div class="p-6 md:p-8 md-down:p-4">
						<div
							class="success-banner rounded-xl px-5 py-4 flex items-start gap-3 mb-8 md-down:mb-6"
						>
							<img
								src="@/assets/images/icons/tick.png"
								alt=""
								class="w-5 h-5 mt-0.5 flex-shrink-0"
							/>
							<div>
								<div class="font-semibold text-green-800 mb-1">
									Đăng ký thành công
								</div>
								<div class="text-sm text-green-700">
									Chúng tôi đã nhận được yêu cầu đăng ký gói
									tiện ích của bạn. <br />
									Luca sẽ xác nhận thanh toán trong vòng 48
									giờ.
								</div>
							</div>
						</div>

						<div
							class="text-lg font-semibold text-neutral-600 mb-4"
						>
							Chi tiết hoá đơn
						</div>
						<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
							<div>
								<div class="text-neutral-700 mb-1">
									Mã hóa đơn
								</div>
								<div class="font-semibold text-neutral-700">
									{{
										contract?.invoice_number || '-'
									}}
								</div>
							</div>
							<div>
								<div class="text-sm text-neutral-400 mb-1">
									Ngày tạo
								</div>
								<div class="font-semibold text-neutral-700">
									{{ dayjs().format('DD/MM/YYYY HH:mm') }}
								</div>
							</div>
						</div>
						<div class="mb-8">
							<div class="text-neutral-700 mb-1">Trạng thái</div>
							<div class="font-semibold text-neutral-700">
								Chờ xác nhận thanh toán
							</div>
						</div>

						<div class="mb-8" v-if="needInvoice">
							<div class="border-t border-neutral-100 pt-8">
								<div
									class="text-lg font-semibold text-neutral-700 mb-4"
								>
									Thông tin xuất hoá đơn
								</div>
								<div
									class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
								>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											MST
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.tax_id || '-' }}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											Công ty
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{
												invoiceForm.company_name || '-'
											}}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											SĐT
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{
												invoiceForm.contact_phone || '-'
											}}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											Email
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.email || '-' }}
										</div>
									</div>
								</div>
								<div>
									<div class="text-sm text-neutral-400 mb-1">
										Địa chỉ
									</div>
									<div class="font-semibold text-neutral-700">
										{{ invoiceForm.address || '-' }}
									</div>
								</div>
							</div>
						</div>

						<div class="border-t border-neutral-100 pt-8">
							<div
								class="text-lg font-semibold text-neutral-700 mb-4"
							>
								Chứng từ thanh toán
							</div>
							<div class="flex items-end gap-4 md-down:flex-col md-down:items-start">
								<img
									v-if="paymentProofPreview"
									:src="paymentProofPreview"
									alt="Chứng từ thanh toán"
									class="max-w-[140px] max-h-40 rounded-lg object-contain border border-neutral-200"
								/>
								<div
									v-else-if="paymentProof"
									class="text-sm text-neutral-600"
								>
									{{ paymentProof.name }}
								</div>
								<button
									v-if="paymentProof"
									type="button"
									class="flex items-center gap-2 text-blue-600 font-semibold text-sm"
									@click="downloadPaymentProof"
								>
									<img
										src="@/assets/images/icons/cloud-download-fill.png"
										alt=""
										class="w-4 h-4"
									/>
									Tải xuống
								</button>
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md-down:p-4 flex justify-end"
					>
						<button
							type="button"
							class="button-p h-12 px-8 font-semibold w-1/2 md-down:w-full"
							@click="goBack"
						>
							Trở về trang chính
						</button>
					</div>
				</div>
			</div>

			<!-- Sidebar summary -->
			<div
				class="col-span-4 md-down:col-span-full md-down:order-1 w-full bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden text-sm"
			>
				<template v-if="!paymentSubmitted">
					<div
						class="summary-header px-5 py-4 flex justify-between items-center gap-2 font-semibold text-neutral-700"
					>
						<span>Gói dung lượng</span>
						<span>{{ summary.label }}</span>
					</div>
					<div class="summary-body px-5 py-4 space-y-3">
						<div
							class="flex justify-between gap-2 text-neutral-700"
						>
							<span>Đơn giá</span>
							<span>{{ formatAmount(summary.unitPrice) }}</span>
						</div>
						<div
							class="flex justify-between gap-2 text-neutral-700"
						>
							<span>Số lượng</span>
							<span>{{ summary.quantity }}</span>
						</div>
						<div
							class="flex justify-between gap-2 text-neutral-700"
						>
							<span>Tổng dung lượng nhận được</span>
							<span class="font-semibold">{{
								summary.totalStorage
							}}</span>
						</div>
					</div>
					<div
						class="px-5 py-4 border-t border-neutral-100 flex justify-between items-center gap-2"
					>
						<span class="font-semibold text-neutral-700"
							>Thành tiền</span
						>
						<span class="font-bold text-neutral-800 text-xl">
							{{ formatAmount(summary.total) }}
						</span>
					</div>
				</template>

				<template v-else>
					<div
						class="px-5 py-3 flex justify-between items-center gap-2 border-b border-neutral-100"
					>
						<span class="font-semibold text-neutral-700"
							>Hoá đơn</span
						>
						<span class="text-neutral-400"
							>Số: {{ contract?.invoice_number }}</span
						>
					</div>
					<div class="current-package px-5 py-4 space-y-1">
						<div class="font-semibold text-neutral-700">
							{{ centerName }}
						</div>
						<div class="text-neutral-600">
							Người tạo: {{ creatorName }}
						</div>
						<div class="text-neutral-600">
							Ngày tạo: {{ dayjs().format('DD/MM/YYYY') }}
						</div>
					</div>
					<div class="px-5 py-4 border-b border-neutral-100">
						<div
							class="flex justify-between gap-2 font-semibold text-neutral-700"
						>
							<span>Gói dung lượng</span>
							<span>{{ summary.label }}</span>
						</div>
					</div>
					<div class="summary-total px-5 py-4 space-y-2">
						<div
							class="flex justify-between gap-2 text-neutral-700"
						>
							<span>Đơn giá</span>
							<span>{{ formatAmount(summary.unitPrice) }}</span>
						</div>
						<div
							class="flex justify-between gap-2 text-neutral-700"
						>
							<span>Số lượng</span>
							<span>{{ summary.quantity }}</span>
						</div>
						<div
							class="border-t border-orange-100 pt-2 flex justify-between items-center gap-2"
						>
							<span class="font-semibold text-neutral-700"
								>Thành tiền</span
							>
							<span class="font-bold text-neutral-800 text-xl">
								{{ formatAmount(summary.total) }}
							</span>
						</div>
					</div>
					<div class="px-6 py-4 flex items-center justify-center">
						<button
							type="button"
							class="flex items-center gap-2 text-blue-600 font-semibold text-xs"
							@click="downloadInvoice"
						>
							<img
								src="@/assets/images/icons/cloud-download-fill.png"
								alt=""
								class="w-4 h-4"
							/>
							Tải xuống hoá đơn
						</button>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<style scoped>
.buying-data__back img {
	transform: rotate(180deg);
}

@media (max-width: 767px) {
	.buying-data__back {
		transform: translateY(-50%);
	}
}

.stepper {
	background: #fff;
	border-bottom: 1px solid #e2e8f0;
	overflow: hidden;
}

.step-item {
	color: #94a3b8;
	background: #fff;
	min-width: 0;
	position: relative;
	cursor: default;
}

.step-item--clickable {
	cursor: pointer;
}

.step-number {
	background: #f1f5f9;
	color: #94a3b8;
	border: 2px solid #cbd5e1;
	box-sizing: border-box;
}

.step-label {
	color: inherit;
}

.step-item--active {
	color: #e5a42b;
}

.step-item--active::after {
	content: '';
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 3px;
	background: #e5a42b;
	z-index: 2;
}

.step-item--active .step-number {
	background: #fff;
	color: #e5a42b;
	border-color: #e5a42b;
}

.step-item--done {
	color: #334155;
}

.step-item--done .step-number {
	background: #e5a42b;
	color: #fff;
	border-color: #e5a42b;
}

.step-divider {
	position: absolute;
	left: 100%;
	top: 50%;
	transform: translateY(-50%);
	height: 100%;
	width: 12px;
	pointer-events: none;
	z-index: 3;
}

@media (max-width: 767px) {
	.stepper {
		border-bottom: none;
	}

	.step-item {
		border-bottom: 1px solid #e2e8f0;
	}

	.step-item--active::after {
		height: 2px;
	}

	.step-number {
		background: #fff;
	}
}

.summary-header {
	background: #fff9f1;
	border-bottom: 1px solid #f1e6d8;
}

.summary-body {
	background: #fff9f1;
}

.summary-total {
	background: #fff7ed;
}

.current-package {
	background: #f1f5f9;
	border-bottom: 1px solid #e1e7ef;
}

.success-banner {
	background: #e8f5ee;
}

@media (max-width: 767px) {
	.summary-total .text-xl {
		font-size: 1.125rem;
	}
}
</style>
